export interface PublicConfig {
  vapidPublicKey: string;
}

export interface PairingResponse {
  status: 'paired';
  deviceToken: string;
}

export interface MobileCall {
  id: string;
  customerName: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  projectName: string;
  contactPreviouslyOpened: boolean;
  expiresAt: string;
  resultExpiresAt: string;
  status:
    | 'created'
    | 'push_sent'
    | 'opened'
    | 'dialer_opened'
    | 'expired'
    | 'failed';
  resultEligible: boolean;
  dealSelection:
    | {
        state: 'required';
        candidates: Array<{
          dealId: number;
          title: string;
          projectName: string;
        }>;
      }
    | { state: 'unavailable'; candidates: [] }
    | null;
  result: MobileCallResultSnapshot | null;
}

export type CallOutcome = 'no_answer' | 'answered' | 'not_interested';

export interface SubmitCallResultInput {
  outcome: CallOutcome;
  nextActivityAt: string | null;
  comment: string;
}

export interface SubmitCallResultResponse {
  status: 'processed' | 'already_processed' | 'manual_review';
  outcome: CallOutcome;
  bitrixActivityId: number;
  nextActivityAt: string | null;
  stageChanged: boolean;
  commentCreated: boolean;
}

export interface MobileCallResultSnapshot {
  state:
    | 'submitting'
    | 'telephony_finished'
    | 'syncing'
    | 'processed'
    | 'manual_review'
    | 'failed'
    | 'unresolved';
  response: SubmitCallResultResponse | null;
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null = null,
    readonly reason: string | null = null,
  ) {
    super(`La solicitud falló (${status})`);
    this.name = 'ApiRequestError';
  }
}

function safeErrorField(value: unknown): string | null {
  return typeof value === 'string' && value.length <= 120 ? value : null;
}

async function jsonRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  if (!response.ok) {
    let code: string | null = null;
    let reason: string | null = null;
    try {
      const body = (await response.json()) as Record<string, unknown>;
      code = safeErrorField(body.error);
      reason = safeErrorField(body.reason);
    } catch {
      // Never retain or expose arbitrary response bodies: they may contain user text.
    }
    throw new ApiRequestError(response.status, code, reason);
  }
  return (await response.json()) as T;
}

export function getPublicConfig(): Promise<PublicConfig> {
  return jsonRequest<PublicConfig>('/api/config');
}

export function pairDevice(
  code: string,
  subscription: PushSubscriptionJSON,
): Promise<PairingResponse> {
  return jsonRequest<PairingResponse>('/api/devices/pair', {
    method: 'POST',
    body: JSON.stringify({ code, subscription }),
  });
}

export function getCall(id: string, deviceToken: string): Promise<MobileCall> {
  return jsonRequest<MobileCall>(`/api/calls/${encodeURIComponent(id)}`, {
    headers: { authorization: `Bearer ${deviceToken}` },
  });
}

export async function updateCallStatus(
  id: string,
  deviceToken: string,
  status: 'opened' | 'dialer_opened',
): Promise<void> {
  await jsonRequest(`/api/calls/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    headers: { authorization: `Bearer ${deviceToken}` },
    body: JSON.stringify({ status }),
  });
}

export function selectCallDeal(
  id: string,
  deviceToken: string,
  dealId: number,
): Promise<MobileCall> {
  return jsonRequest<MobileCall>(
    `/api/calls/${encodeURIComponent(id)}/deal`,
    {
      method: 'POST',
      headers: { authorization: `Bearer ${deviceToken}` },
      body: JSON.stringify({ dealId }),
    },
  );
}

export async function recordContactOpened(
  id: string,
  deviceToken: string,
): Promise<void> {
  await jsonRequest(`/api/calls/${encodeURIComponent(id)}/contact-opened`, {
    method: 'POST',
    headers: { authorization: `Bearer ${deviceToken}` },
  });
}

export function submitCallResult(
  id: string,
  deviceToken: string,
  input: SubmitCallResultInput,
): Promise<SubmitCallResultResponse> {
  return jsonRequest<SubmitCallResultResponse>(
    `/api/calls/${encodeURIComponent(id)}/result`,
    {
      method: 'POST',
      headers: { authorization: `Bearer ${deviceToken}` },
      body: JSON.stringify(input),
    },
  );
}
